import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { Replay } from "@sentry/replay"; // Import Replay from @sentry/replay
import { RouterProvider } from "react-router-dom";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import router from "./Routes";
import { ToasterProvider } from "./contextApi/ToasterContext";
import { Provider } from "react-redux";
import { store } from "./store";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { PublicClientApplication, InteractionStatus } from "@azure/msal-browser";
import { msalConfig } from "./auth-config";
import { setMsalInstance } from "./api-helpers/Spire360ApiClient";

const msalInstance = new PublicClientApplication(msalConfig);

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [new Replay()],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  release: "Spire360",
  environment: process.env.REACT_APP_MODE,
});

const clientTenantId = process.env.REACT_APP_CLIENT_TENANT_ID;

const MsalReadyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts, inProgress } = useMsal();
  const activeAccount = msalInstance.getActiveAccount();

  const tenantAccounts = accounts.filter((acc) => acc.tenantId === clientTenantId);

  const isMsalReady =
    inProgress === InteractionStatus.None &&
    (tenantAccounts.length > 0 || !activeAccount);

  if (!isMsalReady) {
    return <></>;
  }

  return <>{children}</>;
};

const AppLoader: React.FC = () => {
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    const initializeApp = async () => {
      try {
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();

        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
        }

        setMsalInstance(msalInstance);
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize app:", error);
      }
    };

    initializeApp();
  }, []);

  if (!isInitialized) {
    return <></>;
  }

  return (
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <MsalReadyGate>
          <React.StrictMode>
            <ToasterProvider>
              <RouterProvider router={router} />
            </ToasterProvider>
          </React.StrictMode>
        </MsalReadyGate>
      </MsalProvider>
    </Provider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <Sentry.ErrorBoundary>
    <AppLoader />
  </Sentry.ErrorBoundary>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
